/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';

/**
 * Manages all direct interactions with the DOM.
 */
export class DomController {
    // --- Login Gate ---
    public loginGate: HTMLDivElement;
    public loginGateSignInButton: HTMLButtonElement;
    
    // --- Recording & Status ---
    public recordButton: HTMLButtonElement;
    public cancelRecordingButton: HTMLButtonElement;
    public recordingStatus: HTMLDivElement;
    public recordingInterface: HTMLDivElement;
    public liveRecordingTitle: HTMLDivElement;
    public liveWaveformCanvas: HTMLCanvasElement;
    public liveRecordingTimerDisplay: HTMLDivElement;
    public statusIndicatorDiv: HTMLDivElement;
    public recordingModeToggle: HTMLInputElement;
    public sourceViewToggle: HTMLInputElement;
    
    // --- Note Content & Tabs ---
    public noteArea: HTMLDivElement;
    public editorTitle: HTMLDivElement;
    public noteMetadataDisplay: HTMLDivElement;
    public rawTranscription: HTMLDivElement;
    public polishedNote: HTMLDivElement;
    public translatedNote: HTMLDivElement;
    public tabButtons: NodeListOf<HTMLButtonElement>;
    public rawTabButton: HTMLButtonElement;
    public translatedTabButton: HTMLButtonElement;
    public activeTabIndicator: HTMLDivElement;
    public noteContentDivs: NodeListOf<HTMLDivElement>;
    public noteContentWrapper: HTMLDivElement;
    public noteDisplayArea: HTMLDivElement;
    public polishedContainer: HTMLDivElement;
    public rawContainer: HTMLDivElement;
    public translatedContainer: HTMLDivElement;

    // --- Audio Clips ---
    public audioClipsWrapper: HTMLDivElement;
    public audioClipsContainer: HTMLDivElement;

    // --- Header & Main Actions ---
    public appHeader: HTMLElement;
    public newButton: HTMLButtonElement;
    public settingsButton: HTMLButtonElement;
    public allNotesButton: HTMLButtonElement;
    public historyButton: HTMLButtonElement;

    // --- Authentication ---
    public loggedOutContainer: HTMLDivElement;
    public signInButton: HTMLButtonElement;
    public userInfoContainer: HTMLDivElement;
    public userAvatar: HTMLImageElement;
    public userName: HTMLSpanElement;
    public signOutButton: HTMLButtonElement;

    // --- Note Header Tools ---
    public storageStatusIcon: HTMLDivElement;
    public saveNoteButton: HTMLButtonElement;
    public generateTitleFirstLineButton: HTMLButtonElement;
    public generateTitleMagicButton: HTMLButtonElement;
    public toggleFormattingToolbarButton: HTMLButtonElement;
    public toggleFindReplaceButton: HTMLButtonElement;
    public sideBySideButton: HTMLButtonElement;
    public statsButton: HTMLButtonElement;
    public focusModeButton: HTMLButtonElement;
    public exitFocusModeButton: HTMLButtonElement;
    
    // --- Popovers ---
    public settingsPopover: HTMLDivElement;
    public rewritePopover: HTMLDivElement;
    public drivePopover: HTMLDivElement;
    public translatePopover: HTMLDivElement;

    // --- Settings Popover Controls ---
    public modelSelect: HTMLSelectElement;
    public modelCustomInputOption: HTMLDivElement;
    public modelCustomInput: HTMLInputElement;
    public voiceTranscriptionModelSelect: HTMLSelectElement;
    public voiceTranscriptionModelCustomInputOption: HTMLDivElement;
    public voiceTranscriptionModelCustomInput: HTMLInputElement;
    public fastModelSelect: HTMLSelectElement;
    public fastModelCustomInputOption: HTMLDivElement;
    public fastModelCustomInput: HTMLInputElement;
    public titleGeneratorModelSelect: HTMLSelectElement;
    public titleGeneratorModelCustomInputOption: HTMLDivElement;
    public titleGeneratorModelCustomInput: HTMLInputElement;
    public rewritePolishingModelSelect: HTMLSelectElement;
    public rewritePolishingModelCustomInputOption: HTMLDivElement;
    public rewritePolishingModelCustomInput: HTMLInputElement;
    public themeToggleSwitch: HTMLInputElement;
    public autosaveToggle: HTMLInputElement;
    public clearLocalDataButton: HTMLButtonElement;
    public autosaveFrequencyOption: HTMLDivElement;
    public autosaveFrequency: HTMLSelectElement;
    
    // --- Polish Popover Controls ---
    public rewriteButton: HTMLButtonElement;
    public polishToggle: HTMLInputElement;

    // --- Drive Popover Controls ---
    public saveToDriveButton: HTMLButtonElement;
    public savePolishedToggle: HTMLInputElement;
    public saveRawToggle: HTMLInputElement;
    public saveTranslatedToggle: HTMLInputElement;
    public saveTranslatedOption: HTMLDivElement;
    public confirmSaveButton: HTMLButtonElement;
    public saveLocalButton: HTMLButtonElement;
    public prependDateToggle: HTMLInputElement;
    public driveCategoryContainer: HTMLDivElement;
    public driveCategoryInput: HTMLInputElement;
    public driveTagsInput: HTMLInputElement;

    // --- Translate Popover Controls ---
    public translateButton: HTMLButtonElement;
    public translateToggle: HTMLInputElement;
    public translatePolishedToggle: HTMLInputElement;
    public translateRawToggle: HTMLInputElement;
    public translateLanguageSelect: HTMLSelectElement;
    public outputLanguageSelect: HTMLSelectElement;
    public manualTranslateButton: HTMLButtonElement;
    
    // --- Tab Actions ---
    public copyButton: HTMLButtonElement;
    public downloadButton: HTMLButtonElement;

    // --- Markdown Toolbar ---
    public editorToolbarContainer: HTMLDivElement;
    public markdownToolbar: HTMLDivElement;
    public formattingControls: HTMLDivElement;
    public undoButton: HTMLButtonElement;
    public redoButton: HTMLButtonElement;
    public paragraphButton: HTMLButtonElement;
    public h1Button: HTMLButtonElement;
    public h2Button: HTMLButtonElement;
    public h3Button: HTMLButtonElement;
    public h4Button: HTMLButtonElement;
    public boldButton: HTMLButtonElement;
    public italicButton: HTMLButtonElement;
    public strikethroughButton: HTMLButtonElement;
    public codeButton: HTMLButtonElement;
    public ulButton: HTMLButtonElement;
    public olButton: HTMLButtonElement;
    public checklistButton: HTMLButtonElement;
    public indentButton: HTMLButtonElement;
    public outdentButton: HTMLButtonElement;
    public clearFormatButton: HTMLButtonElement;
    public linkButton: HTMLButtonElement;
    public cleanMarkdownButton: HTMLButtonElement;
    public stickyToolbarToggle: HTMLButtonElement;

    // --- Find & Replace ---
    public findReplaceControls: HTMLDivElement;
    public findInput: HTMLInputElement;
    public replaceInput: HTMLInputElement;
    public findPrevButton: HTMLButtonElement;
    public findNextButton: HTMLButtonElement;
    public replaceButton: HTMLButtonElement;
    public replaceAllButton: HTMLButtonElement;
    
    // --- Panes & Overlays ---
    public allNotesOverlay: HTMLDivElement;
    public allNotesPane: HTMLDivElement;
    public closeAllNotesPane: HTMLButtonElement;
    public fetchNotesButton: HTMLButtonElement;
    public notesListContainer: HTMLDivElement;
    public noteViewerPanel: HTMLDivElement;
    public closeNoteViewer: HTMLButtonElement;
    public noteViewerTitle: HTMLHeadingElement;
    public noteViewerContent: HTMLDivElement;
    public notesSortSelect: HTMLSelectElement;
    public notesSearchInput: HTMLInputElement;
    public noteHistoryOverlay: HTMLDivElement;
    public noteHistoryPane: HTMLDivElement;
    public closeNoteHistoryPane: HTMLButtonElement;
    public historyContentContainer: HTMLDivElement;

    // --- Dialogs ---
    public newNoteConfirmOverlay: HTMLDivElement;
    public saveAndCreateNewButton: HTMLButtonElement;
    public discardAndCreateNewButton: HTMLButtonElement;
    public deleteNoteConfirmOverlay: HTMLDivElement;
    public confirmDeleteNoteButton: HTMLButtonElement;
    public cancelDeleteNoteButton: HTMLButtonElement;
    public deleteNoteMessage: HTMLParagraphElement;
    public polishSelectionOverlay: HTMLDivElement;
    public polishSelectionTitle: HTMLHeadingElement;
    public polishOriginalContent: HTMLDivElement;
    public polishPolishedContent: HTMLDivElement;
    public applyPolishButton: HTMLButtonElement;
    public cancelPolishButton: HTMLButtonElement;
    public statsOverlay: HTMLDivElement;
    public statsDialog: HTMLDivElement;
    public closeStatsDialogButton: HTMLButtonElement;
    public wordCountDisplay: HTMLSpanElement;
    public charCountDisplay: HTMLSpanElement;
    public tokenCountDisplay: HTMLSpanElement;
    public tokenUsageButton: HTMLButtonElement;
    public tokenUsageOverlay: HTMLDivElement;
    public tokenUsageDialog: HTMLDivElement;
    public tokenUsageTableBody: HTMLTableSectionElement;
    public exportTokenUsageButton: HTMLButtonElement;
    public closeTokenUsageDialogButton: HTMLButtonElement;
    
    // --- Context Menu ---
    public customContextMenu: HTMLDivElement;

    // --- Action Interface ---
    public actionInterface: HTMLDivElement;
    public actionInterfaceContent: HTMLDivElement;
    public toggleCommandBarButton: HTMLButtonElement;
    public actionInput: HTMLInputElement;
    public actionTestButton: HTMLButtonElement;
    public actionVoiceButton: HTMLButtonElement;
    public actionOutput: HTMLDivElement;

    private liveWaveformCtx: CanvasRenderingContext2D | null;
    private statusClearTimer: number | null = null;
    private noteService: any = null; // Will be injected
    private statusManager: StatusManager;

    constructor() {
        this.statusManager = new StatusManager(this);
        // This is a one-time collection of all DOM elements needed by the app.
        const tabNav = document.querySelector('.tab-navigation')!;
        
        // --- Login Gate ---
        this.loginGate = document.getElementById('login-gate') as HTMLDivElement;
        this.loginGateSignInButton = document.getElementById('login-gate-signin-button') as HTMLButtonElement;

        // --- Recording & Status ---
        this.recordButton = document.getElementById('recordButton') as HTMLButtonElement;
        this.cancelRecordingButton = document.getElementById('cancelRecordingButton') as HTMLButtonElement;
        this.recordingStatus = document.getElementById('recordingStatus') as HTMLDivElement;
        this.recordingInterface = document.querySelector('.recording-interface') as HTMLDivElement;
        this.liveRecordingTitle = document.getElementById('liveRecordingTitle') as HTMLDivElement;
        this.liveWaveformCanvas = document.getElementById('liveWaveformCanvas') as HTMLCanvasElement;
        this.liveRecordingTimerDisplay = document.getElementById('liveRecordingTimerDisplay') as HTMLDivElement;
        this.statusIndicatorDiv = this.recordingInterface.querySelector('.status-indicator') as HTMLDivElement;
        this.recordingModeToggle = document.getElementById('recordingModeToggle') as HTMLInputElement;
        this.sourceViewToggle = document.getElementById('sourceViewToggle') as HTMLInputElement;
        this.liveWaveformCtx = this.liveWaveformCanvas.getContext('2d');

        // --- Note Content & Tabs ---
        this.noteArea = document.querySelector('.note-area') as HTMLDivElement;
        this.editorTitle = document.querySelector('.editor-title') as HTMLDivElement;
        this.noteMetadataDisplay = document.getElementById('noteMetadataDisplay') as HTMLDivElement;
        this.rawTranscription = document.getElementById('rawTranscription') as HTMLDivElement;
        this.polishedNote = document.getElementById('polishedNote') as HTMLDivElement;
        this.translatedNote = document.getElementById('translatedNote') as HTMLDivElement;
        this.tabButtons = tabNav.querySelectorAll('.tab-button');
        this.rawTabButton = tabNav.querySelector('[data-tab="raw"]') as HTMLButtonElement;
        this.translatedTabButton = tabNav.querySelector('[data-tab="translated"]') as HTMLButtonElement;
        this.activeTabIndicator = tabNav.querySelector('.active-tab-indicator') as HTMLDivElement;
        this.noteContentDivs = document.querySelectorAll('.note-content');
        this.noteContentWrapper = document.querySelector('.note-content-wrapper') as HTMLDivElement;
        this.noteDisplayArea = document.getElementById('noteDisplayArea') as HTMLDivElement;
        this.polishedContainer = document.getElementById('polishedContainer') as HTMLDivElement;
        this.rawContainer = document.getElementById('rawContainer') as HTMLDivElement;
        this.translatedContainer = document.getElementById('translatedContainer') as HTMLDivElement;

        // --- Audio Clips ---
        this.audioClipsWrapper = document.getElementById('audioClipsWrapper') as HTMLDivElement;
        this.audioClipsContainer = document.getElementById('audioClipsContainer') as HTMLDivElement;

        // --- Header & Main Actions ---
        this.appHeader = document.querySelector('.app-header') as HTMLElement;
        this.newButton = document.getElementById('newButton') as HTMLButtonElement;
        this.settingsButton = document.getElementById('settingsButton') as HTMLButtonElement;
        this.allNotesButton = document.getElementById('allNotesButton') as HTMLButtonElement;
        this.historyButton = document.getElementById('historyButton') as HTMLButtonElement;
        
        // --- Authentication ---
        this.loggedOutContainer = document.getElementById('loggedOutContainer') as HTMLDivElement;
        this.signInButton = document.getElementById('signInButton') as HTMLButtonElement;
        this.userInfoContainer = document.getElementById('userInfoContainer') as HTMLDivElement;
        this.userAvatar = document.getElementById('userAvatar') as HTMLImageElement;
        this.userName = document.getElementById('userName') as HTMLSpanElement;
        this.signOutButton = document.getElementById('signOutButton') as HTMLButtonElement;
        
        // --- Note Header Tools ---
        this.storageStatusIcon = document.getElementById('storageStatusIcon') as HTMLDivElement;
        this.saveNoteButton = document.getElementById('saveNoteButton') as HTMLButtonElement;
        this.generateTitleFirstLineButton = document.getElementById('generateTitleFirstLineButton') as HTMLButtonElement;
        this.generateTitleMagicButton = document.getElementById('generateTitleMagicButton') as HTMLButtonElement;
        this.toggleFormattingToolbarButton = document.getElementById('toggleFormattingToolbarButton') as HTMLButtonElement;
        this.toggleFindReplaceButton = document.getElementById('toggleFindReplaceButton') as HTMLButtonElement;
        this.exitFocusModeButton = document.getElementById('exitFocusModeButton') as HTMLButtonElement;
        this.focusModeButton = document.getElementById('focusModeButton') as HTMLButtonElement;
        this.sideBySideButton = document.getElementById('sideBySideButton') as HTMLButtonElement;
        this.statsButton = document.getElementById('statsButton') as HTMLButtonElement;

        // --- Popovers ---
        this.settingsPopover = document.getElementById('settingsPopover') as HTMLDivElement;
        this.rewritePopover = document.getElementById('rewritePopover') as HTMLDivElement;
        this.drivePopover = document.getElementById('drivePopover') as HTMLDivElement;
        this.translatePopover = document.getElementById('translatePopover') as HTMLDivElement;

        // --- Settings Popover Controls ---
        this.modelSelect = document.getElementById('modelSelect') as unknown as HTMLSelectElement;
        this.modelCustomInputOption = document.getElementById('modelCustomInputOption') as HTMLDivElement;
        this.modelCustomInput = document.getElementById('modelCustomInput') as HTMLInputElement;
        this.voiceTranscriptionModelSelect = document.getElementById('voiceTranscriptionModelSelect') as unknown as HTMLSelectElement;
        this.voiceTranscriptionModelCustomInputOption = document.getElementById('voiceTranscriptionModelCustomInputOption') as HTMLDivElement;
        this.voiceTranscriptionModelCustomInput = document.getElementById('voiceTranscriptionModelCustomInput') as HTMLInputElement;
        this.fastModelSelect = document.getElementById('fastModelSelect') as unknown as HTMLSelectElement;
        this.fastModelCustomInputOption = document.getElementById('fastModelCustomInputOption') as HTMLDivElement;
        this.fastModelCustomInput = document.getElementById('fastModelCustomInput') as HTMLInputElement;
        this.titleGeneratorModelSelect = document.getElementById('titleGeneratorModelSelect') as unknown as HTMLSelectElement;
        this.titleGeneratorModelCustomInputOption = document.getElementById('titleGeneratorModelCustomInputOption') as HTMLDivElement;
        this.titleGeneratorModelCustomInput = document.getElementById('titleGeneratorModelCustomInput') as HTMLInputElement;
        this.rewritePolishingModelSelect = document.getElementById('rewritePolishingModelSelect') as unknown as HTMLSelectElement;
        this.rewritePolishingModelCustomInputOption = document.getElementById('rewritePolishingModelCustomInputOption') as HTMLDivElement;
        this.rewritePolishingModelCustomInput = document.getElementById('rewritePolishingModelCustomInput') as HTMLInputElement;
        this.themeToggleSwitch = document.getElementById('themeToggle') as HTMLInputElement;
        this.autosaveToggle = document.getElementById('autosaveToggle') as HTMLInputElement;
        this.clearLocalDataButton = document.getElementById('clearLocalDataButton') as HTMLButtonElement;
        this.autosaveFrequencyOption = document.getElementById('autosaveFrequencyOption') as HTMLDivElement;
        this.autosaveFrequency = document.getElementById('autosaveFrequency') as unknown as HTMLSelectElement;

        // --- Polish Popover Controls ---
        this.rewriteButton = document.getElementById('rewriteButton') as HTMLButtonElement;
        this.polishToggle = document.getElementById('polishToggle') as HTMLInputElement;
        
        // --- Drive Popover Controls ---
        this.saveToDriveButton = document.getElementById('saveToDriveButton') as HTMLButtonElement;
        this.savePolishedToggle = document.getElementById('savePolishedToggle') as HTMLInputElement;
        this.saveRawToggle = document.getElementById('saveRawToggle') as HTMLInputElement;
        this.saveTranslatedToggle = document.getElementById('saveTranslatedToggle') as HTMLInputElement;
        this.saveTranslatedOption = document.getElementById('saveTranslatedOption') as HTMLDivElement;
        this.confirmSaveButton = document.getElementById('confirmSaveButton') as HTMLButtonElement;
        this.saveLocalButton = document.getElementById('saveLocalButton') as HTMLButtonElement;
        this.prependDateToggle = document.getElementById('prependDateToggle') as HTMLInputElement;
        this.driveCategoryContainer = document.getElementById('driveCategoryContainer') as HTMLDivElement;
        this.driveCategoryInput = document.getElementById('driveCategoryInput') as HTMLInputElement;
        this.driveTagsInput = document.getElementById('driveTagsInput') as HTMLInputElement;

        // --- Translate Popover Controls ---
        this.translateButton = document.getElementById('translateButton') as HTMLButtonElement;
        this.translateToggle = document.getElementById('translateToggle') as HTMLInputElement;
        this.translatePolishedToggle = document.getElementById('translatePolishedToggle') as HTMLInputElement;
        this.translateRawToggle = document.getElementById('translateRawToggle') as HTMLInputElement;
        this.translateLanguageSelect = document.getElementById('translateLanguageSelect') as unknown as HTMLSelectElement;
        this.outputLanguageSelect = document.getElementById('outputLanguageSelect') as unknown as HTMLSelectElement;
        this.manualTranslateButton = document.getElementById('manualTranslateButton') as HTMLButtonElement;
        
        // --- Tab Actions ---
        this.copyButton = document.getElementById('copyButton') as HTMLButtonElement;
        this.downloadButton = document.getElementById('downloadButton') as HTMLButtonElement;

        // --- Markdown Toolbar ---
        this.editorToolbarContainer = document.querySelector('.editor-toolbar-container') as HTMLDivElement;
        this.markdownToolbar = document.querySelector('.markdown-toolbar') as HTMLDivElement;
        this.formattingControls = document.getElementById('formattingControls') as HTMLDivElement;
        this.undoButton = document.getElementById('undoButton') as HTMLButtonElement;
        this.redoButton = document.getElementById('redoButton') as HTMLButtonElement;
        this.paragraphButton = document.getElementById('paragraphButton') as HTMLButtonElement;
        this.h1Button = document.getElementById('h1Button') as HTMLButtonElement;
        this.h2Button = document.getElementById('h2Button') as HTMLButtonElement;
        this.h3Button = document.getElementById('h3Button') as HTMLButtonElement;
        this.h4Button = document.getElementById('h4Button') as HTMLButtonElement;
        this.boldButton = document.getElementById('boldButton') as HTMLButtonElement;
        this.italicButton = document.getElementById('italicButton') as HTMLButtonElement;
        this.strikethroughButton = document.getElementById('strikethroughButton') as HTMLButtonElement;
        this.codeButton = document.getElementById('codeButton') as HTMLButtonElement;
        this.ulButton = document.getElementById('ulButton') as HTMLButtonElement;
        this.olButton = document.getElementById('olButton') as HTMLButtonElement;
        this.checklistButton = document.getElementById('checklistButton') as HTMLButtonElement;
        this.indentButton = document.getElementById('indentButton') as HTMLButtonElement;
        this.outdentButton = document.getElementById('outdentButton') as HTMLButtonElement;
        this.clearFormatButton = document.getElementById('clearFormatButton') as HTMLButtonElement;
        this.linkButton = document.getElementById('linkButton') as HTMLButtonElement;
        this.cleanMarkdownButton = document.getElementById('cleanMarkdownButton') as HTMLButtonElement;
        this.stickyToolbarToggle = document.getElementById('stickyToolbarToggle') as HTMLButtonElement;

        // --- Find & Replace ---
        this.findReplaceControls = document.getElementById('findReplaceControls') as HTMLDivElement;
        this.findInput = document.getElementById('findInput') as HTMLInputElement;
        this.replaceInput = document.getElementById('replaceInput') as HTMLInputElement;
        this.findPrevButton = document.getElementById('findPrevButton') as HTMLButtonElement;
        this.findNextButton = document.getElementById('findNextButton') as HTMLButtonElement;
        this.replaceButton = document.getElementById('replaceButton') as HTMLButtonElement;
        this.replaceAllButton = document.getElementById('replaceAllButton') as HTMLButtonElement;
        
        // --- Panes & Overlays ---
        this.allNotesOverlay = document.getElementById('allNotesOverlay') as HTMLDivElement;
        this.allNotesPane = document.getElementById('allNotesPane') as HTMLDivElement;
        this.closeAllNotesPane = document.getElementById('closeAllNotesPane') as HTMLButtonElement;
        this.fetchNotesButton = document.getElementById('fetchNotesButton') as HTMLButtonElement;
        this.notesListContainer = document.getElementById('notesListContainer') as HTMLDivElement;
        this.noteViewerPanel = document.getElementById('noteViewerPanel') as HTMLDivElement;
        this.closeNoteViewer = document.getElementById('closeNoteViewer') as HTMLButtonElement;
        this.noteViewerTitle = document.getElementById('noteViewerTitle') as HTMLHeadingElement;
        this.noteViewerContent = document.getElementById('noteViewerContent') as HTMLDivElement;
        this.notesSortSelect = document.getElementById('notesSortSelect') as unknown as HTMLSelectElement;
        this.notesSearchInput = document.getElementById('notesSearchInput') as HTMLInputElement;
        this.noteHistoryOverlay = document.getElementById('noteHistoryOverlay') as HTMLDivElement;
        this.noteHistoryPane = document.getElementById('noteHistoryPane') as HTMLDivElement;
        this.closeNoteHistoryPane = document.getElementById('closeNoteHistoryPane') as HTMLButtonElement;
        this.historyContentContainer = document.getElementById('historyContentContainer') as HTMLDivElement;
        
        // --- Dialogs ---
        this.newNoteConfirmOverlay = document.getElementById('newNoteConfirmOverlay') as HTMLDivElement;
        this.saveAndCreateNewButton = document.getElementById('saveAndCreateNewButton') as HTMLButtonElement;
        this.discardAndCreateNewButton = document.getElementById('discardAndCreateNewButton') as HTMLButtonElement;
        this.deleteNoteConfirmOverlay = document.getElementById('deleteNoteConfirmOverlay') as HTMLDivElement;
        this.confirmDeleteNoteButton = document.getElementById('confirmDeleteNoteButton') as HTMLButtonElement;
        this.cancelDeleteNoteButton = document.getElementById('cancelDeleteNoteButton') as HTMLButtonElement;
        this.deleteNoteMessage = document.getElementById('deleteNoteMessage') as HTMLParagraphElement;
        this.polishSelectionOverlay = document.getElementById('polishSelectionOverlay') as HTMLDivElement;
        this.polishSelectionTitle = document.getElementById('polishSelectionTitle') as HTMLHeadingElement;
        this.polishOriginalContent = document.getElementById('polishOriginalContent') as HTMLDivElement;
        this.polishPolishedContent = document.getElementById('polishPolishedContent') as HTMLDivElement;
        this.applyPolishButton = document.getElementById('applyPolishButton') as HTMLButtonElement;
        this.cancelPolishButton = document.getElementById('cancelPolishButton') as HTMLButtonElement;
        this.statsOverlay = document.getElementById('statsOverlay') as HTMLDivElement;
        this.statsDialog = document.getElementById('statsDialog') as HTMLDivElement;
        this.closeStatsDialogButton = document.getElementById('closeStatsDialogButton') as HTMLButtonElement;
        this.wordCountDisplay = document.getElementById('wordCountDisplay') as HTMLSpanElement;
        this.charCountDisplay = document.getElementById('charCountDisplay') as HTMLSpanElement;
        this.tokenCountDisplay = document.getElementById('tokenCountDisplay') as HTMLSpanElement;
        this.tokenUsageButton = document.getElementById('tokenUsageButton') as HTMLButtonElement;
        this.tokenUsageOverlay = document.getElementById('tokenUsageOverlay') as HTMLDivElement;
        this.tokenUsageDialog = document.getElementById('tokenUsageDialog') as HTMLDivElement;
        this.tokenUsageTableBody = document.getElementById('tokenUsageTableBody') as HTMLTableSectionElement;
        this.exportTokenUsageButton = document.getElementById('exportTokenUsageButton') as HTMLButtonElement;
        this.closeTokenUsageDialogButton = document.getElementById('closeTokenUsageDialogButton') as HTMLButtonElement;
        
        // --- Context Menu ---
        this.customContextMenu = document.getElementById('customContextMenu') as HTMLDivElement;

        // --- Action Interface ---
        this.actionInterface = document.getElementById('actionInterface') as HTMLDivElement;
        this.actionInterfaceContent = document.getElementById('actionInterfaceContent') as HTMLDivElement;
        this.toggleCommandBarButton = document.getElementById('toggleCommandBarButton') as HTMLButtonElement;
        this.actionInput = document.getElementById('actionInput') as HTMLInputElement;
        this.actionTestButton = document.getElementById('actionTestButton') as HTMLButtonElement;
        this.actionVoiceButton = document.getElementById('actionVoiceButton') as HTMLButtonElement;
        this.actionOutput = document.getElementById('actionOutput') as HTMLDivElement;
    }
    
    public updateStatus(html: string) {
        if (this.statusClearTimer) {
            clearTimeout(this.statusClearTimer);
            this.statusClearTimer = null;
        }
        this.recordingStatus.innerHTML = html;
    }

    public updateStatusWithTimeout(html: string, duration = 7000) {
        this.updateStatus(html);
        this.statusClearTimer = window.setTimeout(() => {
            if (this.recordingStatus.innerHTML === html) { // Only clear if the message hasn't changed
                this.updateStatus('Ready');
            }
            this.statusClearTimer = null;
        }, duration);
    }

    public setActiveTab(activeButton: HTMLButtonElement, skipAnimation = false): void {
        if (!activeButton || !this.activeTabIndicator || activeButton.classList.contains('hidden')) return;
        this.tabButtons.forEach(btn => btn.classList.remove("active"));
        activeButton.classList.add("active");
    
        const tabName = activeButton.getAttribute("data-tab");
        
        // Hide/show markdown toolbar
        this.markdownToolbar.classList.toggle('hidden', tabName === 'raw');
    
        // Manage container visibility with classes instead of inline styles
        this.polishedContainer.classList.toggle('visible', tabName === 'note');
        this.rawContainer.classList.toggle('visible', tabName === 'raw');
        this.translatedContainer.classList.toggle('visible', tabName === 'translated');
    
        // Manage content div 'active' state
        this.noteContentDivs.forEach(content => content.classList.remove("active"));
    
        if (tabName === 'raw') {
            this.rawTranscription.classList.add('active');
        } else if (tabName === 'translated') {
            this.translatedNote.classList.add('active');
        } else { // 'note' tab
            this.polishedNote.classList.add('active');
        }
        
        const originalTransition = this.activeTabIndicator.style.transition;
        if (skipAnimation) this.activeTabIndicator.style.transition = "none";
        else this.activeTabIndicator.style.transition = "";

        this.activeTabIndicator.style.left = `${activeButton.offsetLeft}px`;
        this.activeTabIndicator.style.width = `${activeButton.offsetWidth}px`;

        if (skipAnimation) {
            this.activeTabIndicator.offsetHeight; 
            this.activeTabIndicator.style.transition = originalTransition;
        }
    }
    
    public initTheme(theme: 'light' | 'dark'): void {
        document.documentElement.dataset.theme = theme;
        if (this.themeToggleSwitch) {
            this.themeToggleSwitch.checked = theme === 'light';
        }
    }
    
    public updatePolishingButtonVisuals(isPolishingEnabled: boolean): void {
        const button = this.rewriteButton; // Use the new rewrite button
        if (isPolishingEnabled) {
            button.classList.add('active');
        } else {
            button.classList.remove('active');
        }
    }
    
    public updateTranslationButtonVisuals(isTranslationEnabled: boolean, targetLanguage: string): void {
        if (isTranslationEnabled) {
            this.translateButton.classList.add('active');
            this.translateButton.setAttribute('title', `Translating to ${targetLanguage}`);
        } else {
            this.translateButton.classList.remove('active');
            this.translateButton.setAttribute('title', 'Enable Translation');
        }
    }

    public setupCanvasDimensions(): void {
        if (!this.liveWaveformCanvas || !this.liveWaveformCtx) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = this.liveWaveformCanvas.getBoundingClientRect();
        this.liveWaveformCanvas.width = Math.round(rect.width * dpr);
        this.liveWaveformCanvas.height = Math.round(rect.height * dpr);
        this.liveWaveformCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    
    public drawLiveWaveform(analyserNode: AnalyserNode | null, waveformDataArray: Uint8Array | null, isRecording: boolean): number | null {
        if (!analyserNode || !waveformDataArray || !this.liveWaveformCtx || !this.liveWaveformCanvas || !isRecording) {
            return null;
        }

        const drawingId = requestAnimationFrame(() => this.drawLiveWaveform(analyserNode, waveformDataArray, isRecording));
        analyserNode.getByteFrequencyData(waveformDataArray);
        
        const ctx = this.liveWaveformCtx;
        const canvas = this.liveWaveformCanvas;
        const logicalWidth = canvas.clientWidth;
        const logicalHeight = canvas.clientHeight;
        ctx.clearRect(0, 0, logicalWidth, logicalHeight);

        const numBars = Math.floor(analyserNode.frequencyBinCount * 0.5);
        if (numBars === 0) return drawingId;

        const totalBarPlusSpacingWidth = logicalWidth / numBars;
        const barWidth = Math.max(1, Math.floor(totalBarPlusSpacingWidth * 0.7));
        const barSpacing = Math.max(0, Math.floor(totalBarPlusSpacingWidth * 0.3));
        let x = 0;
        
        ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--color-recording').trim() || '#ff3b30';
        for (let i = 0; i < numBars; i++) {
            if (x >= logicalWidth) break;
            const dataIndex = Math.floor(i * (analyserNode.frequencyBinCount / numBars));
            let barHeight = (waveformDataArray[dataIndex] / 255.0) * logicalHeight;
            if (barHeight < 1 && barHeight > 0) barHeight = 1;
            ctx.fillRect(Math.floor(x), Math.round((logicalHeight - barHeight) / 2), barWidth, Math.round(barHeight));
            x += barWidth + barSpacing;
        }
        return drawingId;
    }
    
    public updateLiveTimer(isRecording: boolean, recordingStartTime: number): void {
        if (!isRecording) return;
        const elapsedMs = Date.now() - recordingStartTime;
        const totalSeconds = Math.floor(elapsedMs / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const hundredths = Math.floor((elapsedMs % 1000) / 10);
        this.liveRecordingTimerDisplay.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(hundredths).padStart(2, '0')}`;
    }

    public startLiveDisplay(currentTitle: string): void {
        this.recordingInterface.classList.add('is-live');
        [this.liveRecordingTitle, this.liveWaveformCanvas, this.liveRecordingTimerDisplay].forEach(el => el.style.display = 'block');
        this.setupCanvasDimensions();
        if (this.statusIndicatorDiv) this.statusIndicatorDiv.style.display = 'none';

        const iconElement = this.recordButton.querySelector('.record-button-inner i') as HTMLElement;
        if (iconElement) iconElement.classList.replace('fa-microphone', 'fa-stop');
        
        const placeholder = this.editorTitle.getAttribute('placeholder') || 'Untitled Note';
        this.liveRecordingTitle.textContent = currentTitle && currentTitle !== placeholder ? currentTitle : 'New Recording';
    }

    public stopLiveDisplay(): void {
        this.recordingInterface.classList.remove('is-live');
        [this.liveRecordingTitle, this.liveWaveformCanvas, this.liveRecordingTimerDisplay].forEach(el => el.style.display = 'none');
        if (this.statusIndicatorDiv) this.statusIndicatorDiv.style.display = 'block';

        const iconElement = this.recordButton.querySelector('.record-button-inner i') as HTMLElement;
        if (iconElement) iconElement.classList.replace('fa-stop', 'fa-microphone');
        
        if (this.liveWaveformCtx && this.liveWaveformCanvas) {
            this.liveWaveformCtx.clearRect(0, 0, this.liveWaveformCanvas.width, this.liveWaveformCanvas.height);
        }
    }

    public showLoggedInState(name: string, pictureUrl: string): void {
        this.loggedOutContainer.classList.add('hidden');
        this.userInfoContainer.classList.remove('hidden');
        this.userName.textContent = name;
        this.userAvatar.src = pictureUrl;
        this.userAvatar.alt = `${name}'s avatar`;
    }

    public showLoggedOutState(): void {
        this.loggedOutContainer.classList.remove('hidden');
        this.userInfoContainer.classList.add('hidden');
        this.userName.textContent = '';
        this.userAvatar.src = '';
        this.userAvatar.alt = 'User avatar';
    }

    public getSourceViewState(): boolean {
        return this.sourceViewToggle.checked;
    }

    public setSourceViewState(enabled: boolean): void {
        this.sourceViewToggle.checked = enabled;
        this.updateSourceView(enabled);
    }

    public updateSourceView(isSourceView: boolean): void {
        const activeContent = document.querySelector('.note-content.active') as HTMLElement;
        if (!activeContent) return;

        if (isSourceView) {
            // Store current rendered content
            if (!activeContent.hasAttribute('data-rendered-content')) {
                activeContent.setAttribute('data-rendered-content', activeContent.innerHTML);
            }
            
            // Get the actual raw markdown content from the note service
            const activeTab = document.querySelector('.tab-button.active') as HTMLButtonElement;
            const tabName = activeTab?.getAttribute('data-tab');
            let rawContent = '';
            
            if (this.noteService && this.noteService.currentNote) {
                const currentNote = this.noteService.currentNote;
                switch (tabName) {
                    case 'raw':
                        // Raw transcription is already plain text
                        rawContent = currentNote.rawTranscription || '';
                        break;
                    case 'translated':
                        // Get the raw translated markdown (before rendering)
                        rawContent = currentNote.translatedNote || '';
                        break;
                    case 'note':
                    default:
                        // Get the raw polished markdown (before rendering)
                        rawContent = currentNote.polishedNote || '';
                        break;
                }
            } else {
                // Fallback: get content from DOM if note service not available
                switch (tabName) {
                    case 'raw':
                        rawContent = this.rawTranscription.textContent || '';
                        break;
                    case 'translated':
                        rawContent = this.translatedNote.textContent || '';
                        break;
                    case 'note':
                    default:
                        rawContent = this.polishedNote.textContent || '';
                        break;
                }
            }
            
            // Store the raw content for restoration
            activeContent.setAttribute('data-raw-content', rawContent);
            
            // Show as raw markdown (preserving markdown tags)
            activeContent.innerHTML = `<pre style="white-space: pre-wrap; word-wrap: break-word; font-family: monospace; font-size: 14px; line-height: 1.5; margin: 0; padding: 16px;">${this.escapeHtml(rawContent)}</pre>`;
        } else {
            // Restore rendered content
            const renderedContent = activeContent.getAttribute('data-rendered-content');
            if (renderedContent) {
                activeContent.innerHTML = renderedContent;
            } else {
                // If no stored rendered content, clear and let the note service re-render
                activeContent.innerHTML = '';
                // Trigger a content update event that the note service can listen to
                const event = new CustomEvent('requestContentRerender', {
                    detail: { tabName: document.querySelector('.tab-button.active')?.getAttribute('data-tab') }
                });
                document.dispatchEvent(event);
            }
        }
    }

    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    
    public updateSideBySideButtonState(): void {
        const activeTab = document.querySelector('.tab-button.active') as HTMLButtonElement;
        const tabName = activeTab?.getAttribute('data-tab');
        const shouldEnable = tabName === 'note' || tabName === 'translated';
        this.sideBySideButton.disabled = !shouldEnable;
    }

    public injectNoteService(noteService: any): void {
        this.noteService = noteService;
    }

    public updateRawContent(tabName: string): void {
        let contentElement: HTMLElement | null = null;
        
        switch (tabName) {
            case 'raw':
                contentElement = this.rawTranscription;
                break;
            case 'translated':
                contentElement = this.translatedNote;
                break;
            case 'note':
            default:
                contentElement = this.polishedNote;
                break;
        }
        
        if (contentElement && !contentElement.hasAttribute('data-raw-content')) {
            // Store the current raw content
            const rawContent = contentElement.textContent || '';
            contentElement.setAttribute('data-raw-content', rawContent);
        }
    }
}